/**
 * Compare quest editor state against the last persisted snapshot (successful save or load).
 * Direct uploads (spot thumbnails, spot gallery) update the server before autosave; their
 * keys and client cache-bust generations must be part of the snapshot.
 */
import { galleryPhotoKey, parseGalleryPhotoFile } from "../../shared/gallery-photo.ts";
import { coverKey, parseCoverFile } from "../../shared/quest-cover.ts";
import { pagePhotoKey, parsePagePhotoFile } from "../../shared/page-photo.ts";
import { spotPhotoKeyFromImageUrl } from "../../shared/spot-photo.ts";
import { spotThumbnailKeyFromImageUrl } from "../../shared/spot-thumbnail.ts";
import type { QuestPageBlock } from "../../shared/quest-page-blocks.ts";
import { normalizeQuestSpotImages, toWriteBody, type QuestDetail } from "./types.ts";

export type QuestEditorDirtyOpts = {
  /** Per-checkpoint thumbnail display generation (cache bust after re-upload with same R2 key). */
  thumbBust?: ReadonlyMap<number, number>;
  /** Spot thumbs uploading to R2 (mark dirty before the pending URL lands on the checkpoint). */
  thumbUploadInFlight?: ReadonlySet<number>;
};

function coverKeyFromImageUrl(missionId: string, url: string | null | undefined): string | null {
  if (!url?.trim()) return null;
  const path = url.startsWith("http") ? new URL(url).pathname : (url.split("?")[0] ?? url);
  const prefix = `/api/covers/${encodeURIComponent(missionId)}/`;
  if (!path.startsWith(prefix)) return null;
  const file = path.slice(prefix.length);
  const parsed = parseCoverFile(file);
  if (!parsed) return null;
  return coverKey(missionId, parsed.hash, parsed.ext);
}

function pagePhotoKeyFromImageUrl(missionId: string, slot: number, url: string | null | undefined): string | null {
  if (!url?.trim()) return null;
  const path = url.startsWith("http") ? new URL(url).pathname : (url.split("?")[0] ?? url);
  const prefix = `/api/page-photos/${encodeURIComponent(missionId)}/${slot}/`;
  if (!path.startsWith(prefix)) return null;
  const file = path.slice(prefix.length);
  const parsed = parsePagePhotoFile(file);
  if (!parsed) return null;
  return pagePhotoKey(missionId, slot, parsed.hash, parsed.ext);
}

function galleryPhotoKeyFromImageUrl(
  missionId: string,
  blockSlot: number,
  itemIndex: number,
  url: string | null | undefined,
): string | null {
  if (!url?.trim()) return null;
  const path = url.startsWith("http") ? new URL(url).pathname : (url.split("?")[0] ?? url);
  const prefix = `/api/gallery-photos/${encodeURIComponent(missionId)}/${blockSlot}/${itemIndex}/`;
  if (!path.startsWith(prefix)) return null;
  const file = path.slice(prefix.length);
  const parsed = parseGalleryPhotoFile(file);
  if (!parsed) return null;
  return galleryPhotoKey(missionId, blockSlot, itemIndex, parsed.hash, parsed.ext);
}

function blockMediaSnapshot(block: QuestPageBlock, missionId: string, blockIndex: number): unknown {
  if (block.kind === "gallery") {
    return block.items.map((item, i) => ({
      image_key: item.image ? galleryPhotoKeyFromImageUrl(missionId, blockIndex, i, item.image) : null,
      caption: item.caption ?? null,
    }));
  }
  if (block.kind === "link") {
    return { url: block.url, title: block.title ?? null, image: block.image ?? null };
  }
  return null;
}

function spotMediaSnapshot(q: QuestDetail, opts?: QuestEditorDirtyOpts) {
  return q.checkpoints.map((s, seq) => ({
    thumbnail_key: spotThumbnailKeyFromImageUrl(q.id, seq, s.thumbnail_image ?? null),
    thumb_bust: opts?.thumbBust?.get(seq) ?? 0,
    thumb_upload_in_flight: opts?.thumbUploadInFlight?.has(seq) ?? false,
    image_keys: normalizeQuestSpotImages(s.images).map((img, slot) =>
      spotPhotoKeyFromImageUrl(q.id, seq, slot, img.image),
    ),
    captions: normalizeQuestSpotImages(s.images).map((img) => img.caption ?? null),
  }));
}

/** Stable JSON for dirty detection; omits transient display-only query params on media URLs. */
export function questEditorDirtySnapshot(q: QuestDetail, opts?: QuestEditorDirtyOpts): string {
  const body = toWriteBody(q);
  return JSON.stringify({
    body,
    cover_key: coverKeyFromImageUrl(q.id, q.cover_image ?? q.cover_url ?? null),
    page_photos: (q.page_photos ?? []).map((p, slot) => ({
      image_key: pagePhotoKeyFromImageUrl(q.id, slot, p.image ?? null),
      caption: p.caption ?? null,
    })),
    page_blocks: (q.page_blocks ?? []).map((block, i) => ({
      kind: block.kind,
      media: blockMediaSnapshot(block, q.id, i),
      ...(block.kind === "tip" || block.kind === "quote" ? { text: block.text, attribution: block.kind === "quote" ? block.attribution ?? null : null } : {}),
      ...(block.kind === "quiz"
        ? { question: block.question, answer: block.answer, hint: block.hint ?? null }
        : {}),
    })),
    spot_media: spotMediaSnapshot(q, opts),
  });
}

export function questEditorIsDirty(
  persisted: string,
  q: QuestDetail,
  opts?: QuestEditorDirtyOpts,
): boolean {
  if (!persisted) return false;
  return persisted !== questEditorDirtySnapshot(q, opts);
}
